/**
 * EDIT RELATION DESCRIPTIONS MODAL — modifica le due descrizioni di un legame
 * esistente (FILO-20), senza cancellarlo e ricrearlo: id, storico e flag
 * principale/fissato restano quelli di prima.
 *
 * Le due caselle sono le stesse del drawer di collegamento, dal punto di vista
 * della pagina aperta: → è la descrizione del record della riga (la pagina verso
 * l'altro), ← quella del suo inverse (l'altro verso la pagina), che è il testo
 * che la riga mostra. Si modificano insieme perché vanno lette insieme: correggere
 * «Moglie» senza vedere «Marito» lascerebbe la coppia incoerente.
 *
 * Senza inverse (una relazione scritta da una parte sola) la casella ← non c'è:
 * una modifica non crea righe.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useAppDispatch } from '../store/hooks';
import { updateRelationshipDescriptions } from '../entities/relationship/relationshipThunks';
import type { Relationship } from '../entities/relationship/relationshipTypes';
import { useToast } from './useToast';

interface EditRelationDescriptionsModalProps {
  /** Il record della riga, dal punto di vista della pagina aperta (lato 1). */
  relationship: Relationship;
  /** Il nome dell'altro record, come la riga lo mostra. */
  targetLabel: string;
  onClose: () => void;
  /**
   * Chiamata quando il salvataggio fallisce. Le due PATCH non sono atomiche:
   * se cade la seconda la prima è già scritta, e il chiamante deve rileggere le
   * relazioni per mostrare lo stato vero.
   */
  onFailed: () => void;
}

export function EditRelationDescriptionsModal({
  relationship,
  targetLabel,
  onClose,
  onFailed,
}: EditRelationDescriptionsModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const reverseRelationshipId = relationship.reverseRelationshipId ?? null;

  const [descForward, setDescForward] = useState(relationship.description ?? '');
  const [descReverse, setDescReverse] = useState(relationship.reverseDescription ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await dispatch(updateRelationshipDescriptions({
        relationshipId: relationship.relationshipId,
        description: descForward.trim(),
        reverseRelationshipId,
        reverseDescription: descReverse.trim(),
      })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
      onClose();
    } catch {
      toast({ severity: 'error', message: t('relationship.editDescriptionsError') });
      onFailed();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="tc-head">
        <span className="tc-kicker">{t('relationship.editDescriptions')}</span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <TextField
          label={t('relationship.descForward', { target: targetLabel })}
          value={descForward}
          onChange={(e) => setDescForward(e.target.value)}
          placeholder={t('relationship.descForwardPlaceholder')}
          fullWidth
          className="form-field"
          autoFocus={reverseRelationshipId === null}
        />
        {reverseRelationshipId !== null && (
          <TextField
            label={t('relationship.descReverse', { target: targetLabel })}
            value={descReverse}
            onChange={(e) => setDescReverse(e.target.value)}
            placeholder={t('relationship.descReversePlaceholder')}
            fullWidth
            className="form-field"
            // Il fuoco va qui: è il testo che la riga mostra, quello che si viene
            // a correggere.
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button type="button" onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={handleSave} variant="contained" disabled={saving}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
